import api from '@/api'

// State（状態）
// https://next.vuex.vuejs.org/ja/guide/state.html
// 状態を監視するデータ
const state = () => ({
  data: {},
  detail: {},
  query: {},
  inspection_type: {},
})

// Getters（算出プロパティ）
// https://next.vuex.vuejs.org/ja/guide/getters.html
// コンポーネントから状態を取得するプロパティまたは関数
const getters = {
  inspections: (state) => state.data.results,
  prev: (state) => state.data.previous,
  next: (state) => state.data.next,
  detail: (state) => state.detail,
  query: (state) => state.query,
  inspection_type: (state) => state.inspection_type,
}

// Mutations（状態変更）
// https://next.vuex.vuejs.org/ja/guide/mutations.html
// 状態を変更するメソッド
const mutations = {
  setData: (state, data) => (state.data = data),
  clearData: (state) => (state.data = {}),
  setDetail: (state, detail) => (state.detail = detail),
  clearDetail: (state) => (state.detail = {}),
  setQuery: (state, query) => (state.query = query),
  clearQuery: (state) => (state.query = {}),
  setInspectionType: (state, inspection_type) =>
    (state.inspection_type = inspection_type),
  clearInspectionType: (state) => (state.inspection_type = {}),
}

// Actions（処理）
// https://next.vuex.vuejs.org/ja/guide/actions.html
// コンポーネントからミューテーションを実行する関数
const actions = {
  clearAll({ commit }) {
    return new Promise((resolve) => {
      commit('clearData')
      commit('clearDetail')
      commit('clearQuery')
      commit('clearInspectionType')
      resolve()
    })
  },
  /**
   * データを取得する
   *
   * @return {void}
   */
  async fetchAll({ commit, state }) {
    // パラメーターの値が空の場合は削除する
    Object.keys(state.query).forEach(
      (key) => state.query[key] === '' && delete state.query[key],
    )
    state.query = Object.assign(state.query, {
      inspection_type_id: state.inspection_type.id,
    })
    const response = await api.get('manage/inspections/', {
      params: state.query,
    })
    commit('setData', response.data)
  },
  /**
   * データを1件取得する
   *
   * @param {number}
   * @return {void}
   */
  async fetchData({ commit }, id) {
    const response = await api.get('manage/inspections/' + id + '/')
    commit('setDetail', response.data)
  },
  /**
   * データを追加または更新する
   *
   * @param {object}
   * @return {object}
   */
  async postOrPatch({ dispatch, state }, formData) {
    try {
      // 必要なデータのみにする
      const requiredKeys = ['id', 'name']
      const submitData = Object.fromEntries(
        Object.entries(formData).filter(([key]) => requiredKeys.includes(key)),
      )
      submitData['inspection_type_id'] = state.inspection_type.id

      let response
      if ('id' in submitData && submitData['id']) {
        response = await dispatch('patchData', submitData)
      } else {
        response = await dispatch('postData', submitData)
      }
      return response
    } catch (error) {
      console.error('Failed to post or patch')
      throw error
    }
  },
  /**
   * データを追加する
   *
   * @param {object}
   * @return {object}
   */
  async postData({ commit }, formData) {
    try {
      const response = await api.post('manage/inspections/', formData)
      commit('setDetail', response.data)
      return response.data
    } catch (error) {
      console.error('Failed to post data:', error)
      throw error
    }
  },
  /**
   * データを更新する
   *
   * @param {object}
   * @return {object}
   */
  async patchData({ dispatch }, formData) {
    try {
      const response = await api.patch(
        'manage/inspections/' + formData.id + '/',
        formData,
      )
      dispatch('fetchData', formData.id)
      return response.data
    } catch (error) {
      console.error('Failed to patch data:', error)
      throw error
    }
  },
  /**
   * データを削除する
   *
   * @param {number}
   * @return {number}
   */
  async deleteData({ commit }, id) {
    try {
      await api.delete('manage/inspections/' + id + '/')
      commit('clearData')
      commit('clearDetail')
      return id
    } catch (error) {
      console.error('Failed to delete data:', error)
      throw error
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
